// ==========================================================================
// screens/Tickets.tsx — the dev ticket register (FF-004, 9 Oct 2026)
// Same pattern as Coretel Ops' register: FF-### refs, P0–P3 bands, grouped
// by status, done items dated. Live in the database instead of a markdown
// file. Click a ticket to edit it in place; "New ticket" gets the next ref.
// ==========================================================================
import { useMemo, useRef, useState, type FormEvent } from "react";
import { format } from "date-fns";
import { Plus, X } from "lucide-react";
import { PageHeader } from "@/components/Page";
import { Button, Card, ErrorText } from "@/components/ui";
import { AREAS, PRIORITIES, STATUSES, TYPES, useSaveTicket, useTickets, type Ticket, type TicketDraft, type TicketPriority, type TicketStatus } from "@/lib/tickets";

const prioStyle: Record<TicketPriority, string> = {
  P0: "bg-bad text-white", P1: "bg-primary text-primary-ink", P2: "bg-surface-raised text-ink", P3: "bg-surface-raised text-ink-muted",
};

function Badge({ children, className = "" }: { children: string; className?: string }) {
  return <span className={`inline-block rounded-sm px-1.5 py-0.5 text-xs font-semibold ${className}`}>{children}</span>;
}

const emptyDraft: TicketDraft = { title: "", body: "", type: "new", priority: "P2", status: "open", area: "core" };

function TicketForm({ ticket, onClose }: { ticket?: Ticket; onClose: () => void }) {
  const save = useSaveTicket();
  const [d, setD] = useState<TicketDraft>(ticket ? { title: ticket.title, body: ticket.body, type: ticket.type, priority: ticket.priority, status: ticket.status, area: ticket.area } : emptyDraft);
  const set = <K extends keyof TicketDraft>(k: K, v: TicketDraft[K]) => setD((p) => ({ ...p, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    await save.mutateAsync({ id: ticket?.id, draft: { ...d, title: d.title.trim() } });
    onClose();
  }

  const select = (label: string, k: keyof TicketDraft, opts: { key: string; label: string }[]) => (
    <label className="block space-y-1">
      <span className="text-sm font-medium text-ink-muted">{label}</span>
      <select value={d[k]} onChange={(e) => set(k, e.target.value as never)} className="w-full rounded-md border border-line bg-bg px-3 py-2 text-ink">
        {opts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
    </label>
  );

  return (
    <Card className="space-y-4 border-primary/40">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{ticket ? `${ticket.ref}` : "New ticket"}</h2>
        <button type="button" onClick={onClose} aria-label="Close" className="text-ink-muted hover:text-ink"><X className="h-5 w-5" /></button>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <label className="block space-y-1">
          <span className="text-sm font-medium text-ink-muted">Title</span>
          <input required value={d.title} onChange={(e) => set("title", e.target.value)} className="w-full rounded-md border border-line bg-bg px-3 py-2 text-ink outline-none focus:border-primary" />
        </label>
        <label className="block space-y-1">
          <span className="text-sm font-medium text-ink-muted">Details</span>
          <textarea rows={4} value={d.body} onChange={(e) => set("body", e.target.value)} className="w-full rounded-md border border-line bg-bg px-3 py-2 text-ink outline-none focus:border-primary" />
        </label>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {select("Priority", "priority", PRIORITIES.map((p) => ({ key: p.key, label: `${p.key} · ${p.hint}` })))}
          {select("Status", "status", STATUSES)}
          {select("Type", "type", TYPES)}
          {select("Area", "area", AREAS)}
        </div>
        <ErrorText>{save.error ? "Couldn't save. Try again." : null}</ErrorText>
        <div className="flex gap-3">
          <Button type="submit" disabled={save.isPending} className="lg:w-auto lg:px-6">{save.isPending ? "Saving…" : ticket ? "Save changes" : "Add ticket"}</Button>
          <Button type="button" variant="ghost" onClick={onClose} className="lg:w-auto">Cancel</Button>
        </div>
      </form>
    </Card>
  );
}

export default function Tickets() {
  const tickets = useTickets();
  const save = useSaveTicket();
  const [editing, setEditing] = useState<Ticket | "new" | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [over, setOver] = useState<TicketStatus | null>(null);
  const drag = useRef<Ticket | null>(null);

  const groups = useMemo(() => {
    const by: Record<TicketStatus, Ticket[]> = { doing: [], open: [], blocked: [], done: [] };
    for (const t of tickets.data ?? []) by[t.status].push(t);
    by.done.sort((a, b) => (b.done_on ?? "").localeCompare(a.done_on ?? ""));
    return by;
  }, [tickets.data]);
  const openCount = (tickets.data ?? []).filter((t) => t.status !== "done").length;
  const cols = STATUSES.filter((x) => x.key !== "done" || showDone);

  function drop(status: TicketStatus) {
    const t = drag.current; drag.current = null; setOver(null);
    if (!t || t.status === status) return;
    save.mutate({ id: t.id, draft: { title: t.title, body: t.body, type: t.type, priority: t.priority, status, area: t.area } });
  }

  return (
    <>
      <PageHeader
        title="Tickets"
        subtitle={tickets.data ? `${openCount} open · ${groups.done.length} done · drag a card to change its status` : "Dev work on this app"}
        action={
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setShowDone((v) => !v)} className="rounded-full px-4 py-2 text-sm font-medium ring-1 ring-line hover:ring-primary/40">{showDone ? "Hide done" : `Show done (${groups.done.length})`}</button>
            <button type="button" onClick={() => setEditing("new")} className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-soft"><Plus className="h-4 w-4" /> New ticket</button>
          </div>
        }
      />

      <div className={`grid gap-5 ${showDone ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
        {cols.map((c) => (
          <section key={c.key}
            onDragOver={(e) => { if (drag.current) { e.preventDefault(); setOver(c.key); } }} onDragLeave={() => setOver(null)} onDrop={(e) => { e.preventDefault(); drop(c.key); }}
            className={`rounded-lg p-3 transition ${over === c.key ? "bg-primary/10 ring-2 ring-primary/40" : "bg-surface-raised/70"}`}>
            <h2 className="mb-3 flex items-baseline justify-between px-1 text-[15px] font-semibold">{c.label}<span className="num text-sm font-normal text-ink-muted">{groups[c.key].length}</span></h2>
            <div className="space-y-2.5">
              {groups[c.key].length === 0 && <p className="px-1 py-6 text-center text-sm text-ink-muted">Drop a card here</p>}
              {groups[c.key].map((t) => (
                <button key={t.id} type="button" draggable onDragStart={() => { drag.current = t; }} onDragEnd={() => setOver(null)} onClick={() => setEditing(t)}
                  className="block w-full cursor-grab rounded-md bg-surface p-3.5 text-left shadow-sm ring-1 ring-line/70 transition hover:shadow-soft hover:ring-primary/40 active:cursor-grabbing">
                  <div className="flex items-center justify-between gap-2">
                    <span className="num text-xs font-medium text-ink-muted">{t.ref}</span>
                    <Badge className={prioStyle[t.priority]}>{t.priority}</Badge>
                  </div>
                  <div className="mt-1.5 font-semibold leading-snug">{t.title}</div>
                  {t.body && <div className="mt-1 line-clamp-2 text-sm text-ink-muted">{t.body}</div>}
                  <div className="mt-2 flex items-center gap-2 text-xs text-ink-muted">
                    <span className="rounded-full bg-surface-raised px-2 py-0.5">{AREAS.find((x) => x.key === t.area)?.label}</span>
                    <span>{TYPES.find((x) => x.key === t.type)?.label}</span>
                    {t.done_on && <span className="num ml-auto">{format(new Date(t.done_on), "d MMM")}</span>}
                  </div>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-30 flex items-end justify-center bg-black/30 lg:items-center" onClick={() => setEditing(null)}>
          <div className="max-h-[92vh] w-full overflow-y-auto lg:max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <TicketForm ticket={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} />
          </div>
        </div>
      )}
    </>
  );
}
